/**
 * Order Tracking Page
 * Displays order status and progress after checkout
 */

import { useMemo, useEffect, useState } from 'react'
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Copy,
  MapPin,
  Phone,
  Store,
  Truck,
  ChefHat,
  Package,
  CircleDot,
  MessageCircle,
  AlertCircle,
} from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
  ThemeSwitcher,
} from '@/components/ui'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { clearCart } from '@/store/cartSlice'
import {
  setOrderStatus,
  setEstimatedTime,
  setOrderNumber,
  setTrackingCode,
} from '@/store/orderSlice'
import { formatPhoneNumber } from '@/utils/formatting'
import { useOrderTracking } from '@/hooks/useOrderTracking'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import {
  getTrackingSteps,
  getTrackingStepIndex,
  type OrderStatus,
  type OrderType,
  type TrackingStep,
} from '@/types/order'
import { restaurantConfig } from '@/config/restaurant'

interface OrderTrackingPageProps {
  navigate: (path: string) => void
  /** Order ID from URL parameter - can be used to fetch order from API */
  orderId?: string
  /** Tracking code from URL parameter - for public shareable links */
  trackingCode?: string
}

// Note: orderId prop is available for future API integration to fetch order details by ID

/**
 * Get the icon for a tracking step
 */
function getStepIcon(stepId: string, isActive: boolean, isCompleted: boolean) {
  const iconClass = `h-5 w-5 ${
    isCompleted
      ? 'text-green-600'
      : isActive
        ? 'text-primary'
        : 'text-muted-foreground'
  }`

  switch (stepId) {
    case 'confirmed':
      return <CheckCircle2 className={iconClass} />
    case 'preparing':
      return <ChefHat className={iconClass} />
    case 'ready':
      return <Package className={iconClass} />
    case 'out-for-delivery':
      return <Truck className={iconClass} />
    case 'delivered':
    case 'completed':
      return <CheckCircle2 className={iconClass} />
    default:
      return <CircleDot className={iconClass} />
  }
}

/**
 * Progress indicator component showing order status steps.
 *
 * Layout: each step is a row with [icon + line segment] on the left and text on the right.
 * The connecting line renders between icons (not through them).
 */
function OrderProgressIndicator({
  steps,
  currentStepIndex,
}: {
  steps: TrackingStep[]
  currentStepIndex: number
}) {
  return (
    <div>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1
        // When the active step is the last one, all steps are done — show everything green
        const allDone = currentStepIndex === steps.length - 1
        const isCompleted = allDone ? index <= currentStepIndex : index < currentStepIndex
        const isActive = allDone ? false : index === currentStepIndex
        const isPending = index > currentStepIndex

        // Line below this icon is green if this step is completed
        const lineGreen = isCompleted

        return (
          <div
            key={step.id}
            className={`flex gap-4 transition-opacity ${
              isPending ? 'opacity-40' : 'opacity-100'
            }`}
          >
            {/* Left column: icon circle + connecting line */}
            <div className="flex flex-col items-center">
              {/* Pulse ring wrapper for active step */}
              <div className="relative shrink-0">
                {isActive && (
                  <span className="absolute inset-0 rounded-full animate-ping-slow bg-primary/20" />
                )}
                <div
                  className={`relative flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all ${
                    isCompleted
                      ? 'border-green-500 bg-green-50 dark:bg-green-500/10'
                      : isActive
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-background'
                  }`}
                >
                  {getStepIcon(step.id, isActive, isCompleted)}
                </div>
              </div>

              {/* Connecting line to next step */}
              {!isLast && (
                <div
                  className={`w-0.5 flex-1 min-h-4 mt-1.5 mb-1.5 rounded-full transition-colors duration-500 ${
                    lineGreen ? 'bg-green-500' : 'bg-border'
                  }`}
                />
              )}
            </div>

            {/* Right column: text content */}
            <div className={`flex-1 pt-2 ${!isLast ? 'pb-4' : ''}`}>
              <div
                className={`font-semibold ${
                  isCompleted
                    ? 'text-green-700 dark:text-green-400'
                    : isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {step.description}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}


/**
 * Skeleton loading state for the tracking page
 */
function OrderTrackingSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header skeleton */}
        <div className="flex items-center gap-2 mb-6">
          <Skeleton className="h-9 w-32" />
        </div>

        {/* Main card skeleton */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="h-5 w-32 mt-2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-4">
                  <Skeleton className="h-11 w-11 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ETA skeleton */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex items-center justify-center gap-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order summary skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex justify-between">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/**
 * Main Order Tracking Page component
 */
export function OrderTrackingPage({ navigate, orderId, trackingCode: trackingCodeProp }: OrderTrackingPageProps) {
  const dispatch = useAppDispatch()
  const order = useAppSelector((s) => s.order)
  const reduxTrackingCode = useAppSelector((s) => s.order.trackingCode)

  // The effective tracking code: prefer the URL param (public link), fall back to Redux state
  const effectiveTrackingCode = trackingCodeProp || reduxTrackingCode

  // Public link = opened via a shared tracking URL (has trackingCode in URL params)
  const isPublicLink = !!trackingCodeProp
  const { data: publicOrderData } = menuWidgetApi.publicOrderTracking.get(
    effectiveTrackingCode ? { trackingCode: effectiveTrackingCode } : undefined,
    { skip: !effectiveTrackingCode }
  )

  // Cancel order mutation hook (must be declared before any early returns)
  const [addCancelOrder] = menuWidgetApi.cancelOrder.add()

  // Track if we came back from a Stripe 3D Secure redirect
  const [stripeRedirectError, setStripeRedirectError] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  // Cancellation state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [timeLeftMs, setTimeLeftMs] = useState<number | null>(null)

  // Handle return from Stripe 3D Secure redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentIntent = params.get('payment_intent')
    const redirectStatus = params.get('redirect_status')

    if (!paymentIntent) return

    if (redirectStatus === 'succeeded') {
      // Payment succeeded after 3D Secure redirect — finalize the order
      dispatch(clearCart())
      dispatch(setOrderStatus('confirmed'))
      const eta = new Date(Date.now() + 30 * 60 * 1000).toISOString()
      dispatch(setEstimatedTime(eta))

      // Clean up Stripe params from the URL without triggering a navigation
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
    } else if (redirectStatus === 'failed') {
      // Payment failed after redirect — show error and allow retry
      setStripeRedirectError(
        'Your payment was not completed. Please go back to checkout and try again.'
      )
      // Clean up params from the URL
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
    }
    // If redirect_status is 'processing', payment is still in-flight — show normal tracking
  }, [dispatch])

  // Hydrate Redux from public API data — ensures orderNumber/trackingCode persist
  // to localStorage so reloads don't lose them and isPublicLink stays stable.
  useEffect(() => {
    if (!publicOrderData) return
    if (publicOrderData.order_number) {
      dispatch(setOrderNumber(publicOrderData.order_number))
    }
    if (publicOrderData.tracking_code || trackingCodeProp) {
      dispatch(setTrackingCode(publicOrderData.tracking_code || trackingCodeProp!))
    }
    if (publicOrderData.status) {
      dispatch(setOrderStatus(publicOrderData.status as OrderStatus))
    }
    if (publicOrderData.estimated_ready_at) {
      dispatch(setEstimatedTime(publicOrderData.estimated_ready_at))
    }
  }, [publicOrderData, trackingCodeProp, dispatch])

  // Safety net: clear cart when viewing a confirmed order (handles edge cases
  // where finalizeOrder didn't fire, e.g. page crash, stale closure, etc.)
  useEffect(() => {
    const status = order.orderStatus
    if (status === 'confirmed' || status === 'preparing' || status === 'ready' || status === 'completed') {
      dispatch(clearCart())
    }
  }, [order.orderStatus, dispatch])

  // Real-time tracking via Firebase RTDB with polling fallback
  const { isRealtime, isPolling } = useOrderTracking({
    orderId: orderId || order.orderId,
    trackingCode: effectiveTrackingCode,
    enabled: !!(orderId || order.orderId || effectiveTrackingCode),
  })

  const handleCopyTrackingLink = () => {
    const trackingUrl = `${window.location.origin}/tracking/${effectiveTrackingCode}`
    navigator.clipboard.writeText(trackingUrl).then(() => {
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    })
  }

  // Determine if cancellation is possible — must be in a cancellable status
  // AND within the grace period. The live RTDB status takes priority over
  // cached API data (admin may have advanced the order since the API call).
  const cancellableStatuses = ['pending', 'confirmed']
  const liveStatus = order.orderStatus !== 'idle' ? order.orderStatus : publicOrderData?.status
  const statusAllowsCancel = cancellableStatuses.includes(liveStatus || '')
  const canCancel = statusAllowsCancel && (publicOrderData?.can_cancel ?? true)
  const cancelDeadline = publicOrderData?.cancel_deadline || null

  // Countdown timer for grace period — always uses backend cancel_deadline
  useEffect(() => {
    if (!cancelDeadline) return

    const deadlineMs = new Date(cancelDeadline).getTime()
    const update = () => {
      const remaining = Math.max(0, deadlineMs - Date.now())
      setTimeLeftMs(remaining)
    }
    update()
    const t = setInterval(update, 1000)
    return () => clearInterval(t)
  }, [cancelDeadline])

  const showCancelButton = canCancel && timeLeftMs !== null && timeLeftMs > 0

  // Format countdown as m:ss
  const formatCountdown = (ms: number) => {
    const totalSec = Math.ceil(ms / 1000)
    const min = Math.floor(totalSec / 60)
    const sec = totalSec % 60
    return `${min}:${sec.toString().padStart(2, '0')}`
  }

  // Handle cancel order
  const handleCancel = async () => {
    if (!effectiveTrackingCode) return
    setIsCancelling(true)
    setCancelError(null)

    try {
      await addCancelOrder({ trackingCode: effectiveTrackingCode }).unwrap()
      dispatch(setOrderStatus('cancelled'))
      setShowCancelConfirm(false)
    } catch (err: unknown) {
      const msg = (err as { data?: { error?: string } })?.data?.error || 'Failed to cancel order'
      setCancelError(msg)
    } finally {
      setIsCancelling(false)
    }
  }

  // Merge Redux state with public API data (public API data used when no Redux state)
  const orderNumber = order.orderNumber || publicOrderData?.order_number || null
  const orderType = order.orderType || publicOrderData?.order_type || null
  const orderStatus = order.orderStatus !== 'idle' ? order.orderStatus : (publicOrderData?.status || null)
  const estimatedTime = order.estimatedTime || publicOrderData?.estimated_ready_at || null
  const customerInfo = order.customerInfo
  const deliveryInfo = order.deliveryInfo

  // For demo purposes, if no order exists, show a sample order
  const hasOrder = !!orderNumber

  // Determine tracking steps based on order type
  const effectiveOrderType: OrderType = orderType || 'pickup'
  const effectiveStatus: OrderStatus = orderStatus || 'confirmed'

  const steps = useMemo(
    () => getTrackingSteps(effectiveOrderType),
    [effectiveOrderType]
  )

  const currentStepIndex = useMemo(
    () => getTrackingStepIndex(effectiveStatus, effectiveOrderType),
    [effectiveStatus, effectiveOrderType]
  )

  // Format estimated time for display
  const formattedEta = useMemo(() => {
    if (!estimatedTime) return null
    try {
      const date = new Date(estimatedTime)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch {
      return estimatedTime
    }
  }, [estimatedTime])

  // Calculate time remaining (mock for demo)
  const timeRemaining = useMemo(() => {
    if (!estimatedTime) return null
    try {
      const eta = new Date(estimatedTime)
      const now = new Date()
      const diffMs = eta.getTime() - now.getTime()
      if (diffMs <= 0) return 'Any moment now'
      const diffMins = Math.ceil(diffMs / 60000)
      if (diffMins < 60) return `${diffMins} min`
      const hours = Math.floor(diffMins / 60)
      const mins = diffMins % 60
      return `${hours}h ${mins}m`
    } catch {
      return null
    }
  }, [estimatedTime])

  // Public links: show skeleton until we have real data — prevents blank/flicker on mobile
  if (isPublicLink && !hasOrder) {
    return <OrderTrackingSkeleton />
  }

  // If no order and status is idle (and no Stripe redirect error), show a "no order" state
  // Don't show this when navigating via a public tracking link (wait for API to load)
  if (!hasOrder && effectiveStatus === 'idle' && !stripeRedirectError && !isPublicLink) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-6 max-w-3xl">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/menu')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Menu
            </Button>
            <ThemeSwitcher />
          </div>

          <Card className="text-center py-12">
            <CardContent>
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Active Order</h2>
              <p className="text-muted-foreground mb-6">
                You don&apos;t have any orders to track right now.
              </p>
              <Button variant="black" onClick={() => navigate('/menu')}>
                Start Ordering
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Show Stripe redirect payment failure page
  if (stripeRedirectError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-6 max-w-3xl">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/checkout')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Checkout
            </Button>
          </div>

          <Card className="text-center py-12">
            <CardContent>
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">{stripeRedirectError}</p>
              <Button variant="black" onClick={() => navigate('/checkout')}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Check if order is in error or cancelled state
  const isErrorState = effectiveStatus === 'error' || effectiveStatus === 'cancelled'

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/menu')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Menu
          </Button>
          <ThemeSwitcher />
        </div>

        {/* Order Status Card */}
        <Card className="mb-6 overflow-hidden">
          {/* Success banner */}
          {currentStepIndex >= 0 && !isErrorState && (
            <div className="bg-green-50 border-b border-green-100 px-6 py-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">
                  {effectiveStatus === 'completed' ? 'Order Complete' : 'Order Confirmed'}
                </span>
              </div>
            </div>
          )}

          {/* Error banner */}
          {isErrorState && (
            <div className="bg-red-50 border-b border-red-100 px-6 py-3">
              <div className="flex items-center gap-2 text-red-700">
                <span className="font-medium">
                  {effectiveStatus === 'cancelled'
                    ? 'Order Cancelled'
                    : 'Something went wrong'}
                </span>
              </div>
            </div>
          )}

          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="text-xl">Order #{orderNumber || 'N/A'}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                  {effectiveOrderType === 'delivery' ? 'Delivery' : 'Pickup'}
                  {(isRealtime || isPolling) && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/60">
                      <span className={`h-1.5 w-1.5 rounded-full ${isRealtime ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
                      {isRealtime ? 'Live' : 'Updating'}
                    </span>
                  )}
                </p>
              </div>
              <Badge
                variant={isErrorState ? 'destructive' : 'secondary'}
                className="w-fit"
              >
                {effectiveStatus
                  .split('-')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ')}
              </Badge>
            </div>
          </CardHeader>

          {/* Share tracking link */}
          {effectiveTrackingCode && (
            <div className="px-6 pb-3 border-t border-border pt-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground truncate">
                  Share:{' '}
                  <span className="font-mono text-xs">
                    {window.location.origin}/tracking/{effectiveTrackingCode}
                  </span>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  onClick={handleCopyTrackingLink}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {linkCopied ? 'Copied!' : 'Copy link'}
                </Button>
              </div>
            </div>
          )}

          <CardContent className="pt-4">
            {!isErrorState && (
              <OrderProgressIndicator
                steps={steps}
                currentStepIndex={currentStepIndex}
              />
            )}
          </CardContent>
        </Card>

        {/* Cancellation Section */}
        {showCancelButton && !isErrorState && (
          <Card className="mb-6 border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/20">
            <CardContent className="py-4">
              {!showCancelConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Changed your mind?</p>
                    <p className="text-xs text-muted-foreground">
                      You can cancel within {formatCountdown(timeLeftMs!)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-300 hover:bg-orange-100"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    Cancel Order
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Are you sure you want to cancel?</p>
                  <p className="text-xs text-muted-foreground">
                    Your payment will be refunded. Time remaining: {formatCountdown(timeLeftMs!)}
                  </p>
                  {cancelError && (
                    <p className="text-xs text-destructive">{cancelError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleCancel}
                      disabled={isCancelling}
                    >
                      {isCancelling ? 'Cancelling...' : 'Yes, Cancel Order'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowCancelConfirm(false)
                        setCancelError(null)
                      }}
                      disabled={isCancelling}
                    >
                      Keep Order
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Estimated Time Card — hide when order is completed */}
        {(formattedEta || timeRemaining) && !isErrorState && effectiveStatus !== 'completed' && (
          <Card className="mb-6">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-4">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">
                    {effectiveOrderType === 'delivery'
                      ? 'Estimated Delivery'
                      : 'Ready for Pickup'}
                  </div>
                  <div className="text-2xl font-bold">
                    {formattedEta || timeRemaining}
                  </div>
                  {timeRemaining && formattedEta && (
                    <div className="text-sm text-muted-foreground">
                      {timeRemaining} remaining
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Location Card */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {effectiveOrderType === 'delivery' ? (
                <>
                  <Truck className="h-4 w-4" />
                  Delivery Address
                </>
              ) : (
                <>
                  <Store className="h-4 w-4" />
                  Pickup Location
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {effectiveOrderType === 'delivery' && deliveryInfo ? (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">{deliveryInfo.street}</div>
                  {deliveryInfo.apartmentNumber && (
                    <div className="text-sm text-muted-foreground">
                      Apt {deliveryInfo.apartmentNumber}
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    {deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}
                  </div>
                  {deliveryInfo.deliveryInstructions && (
                    <div className="text-sm text-muted-foreground mt-2 italic">
                      &quot;{deliveryInfo.deliveryInstructions}&quot;
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">{restaurantConfig.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {restaurantConfig.address.street}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {restaurantConfig.address.city}, {restaurantConfig.address.state}{' '}
                    {restaurantConfig.address.zipCode}
                  </div>
                  {restaurantConfig.phone && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                      <Phone className="h-3 w-3" />
                      {formatPhoneNumber(restaurantConfig.phone)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        {customerInfo && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">
                    {customerInfo.firstName} {customerInfo.lastName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span className="font-medium">
                    {formatPhoneNumber(customerInfo.phone)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{customerInfo.email}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Support Card */}
        <Card className="mb-6">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="font-medium">Need help?</div>
                  <div className="text-sm text-muted-foreground">
                    Contact us for support
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {restaurantConfig.phone && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() =>
                      window.open(`tel:${restaurantConfig.phone}`, '_self')
                    }
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() =>
                    window.open(`mailto:${restaurantConfig.email || 'support@restaurant.com'}`)
                  }
                >
                  <MessageCircle className="h-4 w-4" />
                  Email
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Menu Button */}
        <div className="text-center">
          <Button
            variant="black"
            size="lg"
            className="w-full sm:w-auto rounded-full px-8"
            onClick={() => navigate('/menu')}
          >
            Back to Menu
          </Button>
        </div>
      </div>
    </div>
  )
}

export { OrderTrackingSkeleton }
